import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { useCreateEntity } from 'src/features/entity/api/useCreateEntity';
import { useGetIdentifier } from 'src/features/membership/api/identifier/useGetIdentifier';
import IdentifierForm from 'src/features/membership/components/forms/identifier/IdentifierForm';
import {
    membershipIdentifierFormSchema,
    MembershipIdentifierFormSchemaValues,
} from 'src/features/membershipType/formSchema';
import { IDENTIFIER_TYPE, MembershipIdentifier } from 'src/features/membership/types';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { cn } from 'src/utils/shadcn';
import { useParking } from 'src/providers/ParkingProvider';

interface IdentifierFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedIdentifier?: MembershipIdentifier;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: MembershipIdentifierFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
    isClient?: boolean;
    membershipId?: string;
}

function IdentifierFormModal<TData>({
    selectedIdentifier,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
    isClient = false,
    membershipId = '',
}: IdentifierFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const { parkingId } = useParking();
    const { data: identifier } = useGetIdentifier({
        parkingId,
        membershipId,
        identifierId: selectedIdentifier?.id ?? '',
        // We only want to fetch if we're in edit
        config: { enabled: !!selectedIdentifier?.id && isModalOpen },
    });

    const onError = useOnError();

    const defaultValues = React.useMemo(
        () =>
            selectedIdentifier
                ? {
                      ...selectedIdentifier,
                      owner: selectedIdentifier.owner?.id,
                  }
                : {
                      ...(isClient ? { identifier: { value: undefined, type: IDENTIFIER_TYPE.LICENSE_PLATE } } : {}),
                  },
        [selectedIdentifier, isClient],
    );

    const form = useForm<MembershipIdentifierFormSchemaValues>({
        resolver: zodResolver(membershipIdentifierFormSchema),
        defaultValues,
    });

    const { mutate: createEntity } = useCreateEntity({});
    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        form.reset(defaultValues);
    }, [identifier, isModalOpen, defaultValues, form]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: MembershipIdentifierFormSchemaValues) => {
            if (values.entity) {
                createEntity(
                    { parkingId, ...values.entity },
                    {
                        onSuccess: (entity) => {
                            const newValues = {
                                ...values,
                                owner: entity.id,
                            };
                            mutate(
                                newValues,
                                () => setIsModalOpen(false),
                                (error) => onError(error, form.setError),
                            );
                        },
                    },
                );
            } else
                mutate(
                    values,
                    () => setIsModalOpen(false),
                    (error) => onError(error, form.setError),
                );
        },
        [mutate, createEntity, onError, form.setError, parkingId],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader className="flex-row gap-2">
                    <SquareIcon icon={icon} />
                    <div>
                        <DialogTitle>{title}</DialogTitle>
                        <DialogDescription>{subtitle}</DialogDescription>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <IdentifierForm form={form} onSubmit={onSubmit} isClient={isClient} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default IdentifierFormModal;
